/** Query keys for voice profiles and voice identification.
 *
 * Every key carries the JWT's organization id (`oid`). A reseller switching
 * customer already triggers `queryClient.clear()` plus a reload, but scoping
 * the key makes cross-tenant reuse impossible rather than merely unlikely:
 * organization B can never read a cache entry written for organization A,
 * even if some future switch path forgets to clear. */

import { TokenExtension } from '@utils/extensions/token.extension.ts';

/** The caller's organization id, or 0 when there is no usable token. */
export const currentOrganizationId = (): number =>
  TokenExtension.get()?.decoded?.oid ?? 0;

export const VOICE_PROFILE_QUERY_KEYS = {
  /** Readiness and limits for the caller's organization. */
  capabilities: () => ['voice-profile-capabilities', currentOrganizationId()],

  /** One directory speaker's enrolled samples. */
  samples: (speakerId: number) => [
    'voice-profile-samples',
    currentOrganizationId(),
    speakerId
  ],

  /** Share only the in-flight clean-speech plan for one source voice. */
  samplePlan: (sessionId: string, documentId: string, speakerLabel: string) => [
    'voice-sample-plan',
    currentOrganizationId(),
    sessionId,
    documentId,
    speakerLabel
  ],

  /** Job status and per-label suggestions for one document. */
  identifications: (sessionId: string, documentId: string) => [
    'voice-identifications',
    currentOrganizationId(),
    sessionId,
    documentId
  ],

  /** Original diarization labels mapped to source-audio milliseconds. */
  timeline: (sessionId: string, documentId: string) => [
    'voice-identification-timeline',
    currentOrganizationId(),
    sessionId,
    documentId
  ]
} as const;
